import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Copy, FilmSlate, MagnifyingGlass, ShareNetwork } from "@phosphor-icons/react";
import { PRODUCT_VIDEOS } from "../data/productVideos";
import { copyToClipboard } from "../lib/clipboard";

const LANGS = ["en", "ru", "fr"];

// v414.17 — Product Videos library: 38 products x EN/RU/FR explainer clips.
// Inline 9:16 players, language tabs swap the source without reload.
// preload="none" so nothing downloads until a distributor presses play.
// v414.19 — instant search box filters cards by product name.
export default function ProductVideosPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return PRODUCT_VIDEOS;
    return PRODUCT_VIDEOS.filter((v) => v.name.toLowerCase().includes(needle));
  }, [q]);
  return (
    <div className="space-y-6" data-testid="product-videos-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2.5">
          <FilmSlate size={26} weight="bold" className="text-[#8a6a1c]" />
          {t("videos.title")}
        </h1>
        <p className="text-sm text-zinc-600 mt-1">
          {t("videos.subtitle", { count: PRODUCT_VIDEOS.length })}
        </p>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("videos.search_placeholder")}
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-zinc-300 bg-white text-sm focus:outline-none focus:border-[#c9a24a] focus:ring-1 focus:ring-[#c9a24a]"
          data-testid="videos-search-input"
        />
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="videos-search-empty">
          {t("videos.search_empty", { query: q.trim() })}
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {shown.map((v) => (
            <VideoCard key={v.name} video={v} />
          ))}
        </div>
      )}
    </div>
  );
}

function VideoCard({ video }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState("en");
  const slug = video.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const shareText = t("videos.share_text", { name: video.name, lang: lang.toUpperCase() });

  // v414.20 — share the selected language's clip: native share sheet on
  // mobile (WhatsApp / SMS / email), clipboard fallback on desktop.
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: video.name, text: shareText, url: video[lang] });
        return;
      } catch {
        /* user closed the sheet — fall through to copy */
      }
    }
    const ok = await copyToClipboard(`${shareText}\n${video[lang]}`);
    toast[ok ? "success" : "error"](ok ? t("videos.copied") : t("videos.copy_failed"));
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(video[lang]);
    toast[ok ? "success" : "error"](ok ? t("videos.copied") : t("videos.copy_failed"));
  };

  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden flex flex-col"
      data-testid={`video-card-${slug}`}
    >
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-start justify-between gap-1.5">
          <h2 className="text-sm font-semibold text-zinc-950 leading-snug truncate" title={video.name}>
            {video.name}
          </h2>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={copyLink}
              title={t("videos.copy_link")}
              className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
              data-testid={`video-copy-${slug}`}
            >
              <Copy size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={share}
              title={t("videos.share")}
              className="w-7 h-7 rounded-md flex items-center justify-center text-[#8a6a1c] hover:bg-[#fbf6e7] transition-colors"
              data-testid={`video-share-${slug}`}
            >
              <ShareNetwork size={14} weight="bold" />
            </button>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1">
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              data-testid={`video-lang-${slug}-${l}`}
              className={`h-7 rounded-md border text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                lang === l
                  ? "bg-zinc-950 text-[#c9a24a] border-[#c9a24a]"
                  : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50 hover:text-zinc-800"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="px-3 pb-3">
        <video
          key={video[lang]}
          src={video[lang]}
          controls
          playsInline
          preload="none"
          className="w-full aspect-[9/16] rounded-lg bg-zinc-950 object-contain"
          data-testid={`video-player-${slug}`}
        />
      </div>
    </div>
  );
}
